var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
import { Expose, Type } from 'class-transformer';
import { GkvTextField } from '../enums/gkv-text-field.enum.js';
/**
 * One §302 Abrechnungsfall the run has looked at — billed or set aside.
 *
 * **A Fall is a `TreatmentPlan`**, because a Verordnungsblatt is, and it is
 * billed exactly once after its end: the Urbeleg is the Verordnungs-Original
 * that goes out with the Abrechnung, and it cannot be enclosed a second time.
 *
 * ⚠ Both the billed and the skipped Fälle are on this list. A view that showed
 * only what will go out would answer half the question a practice is asking —
 * *„was hält die übrigen auf?"* is the other half, and it is the half that
 * costs money.
 */
export class GkvBillingCandidateRowDto {
    treatmentPlanId;
    patientId;
    patientFirstName;
    patientLastName;
    /** Muster 13 Feld 16 — decides both the price lineage and the billing IK. */
    heilmittelbereich;
    /** The card IK off Muster 13 Feld 08; `null` is itself a skip reason. */
    /** Spec name: „Kostenträgerkennung". */
    kostentraegerCode;
    /**
     * The terminal node of the `VKG+01` chain — `SLLA.FKT` / `SLGA.FKT`.
     *
     * ⚠ **Not always the card IK.** BKK firmus (`103121137`) bills through BKK
     * firmus/Ost (`102529638`), and 38 card-IKs in the committed corpus chain
     * more than one hop. `null` while the Fall could not be addressed.
     */
    kostentraegerIk;
    kostentraegerName;
    /** The Empfänger this Fall's Nutzdatendatei goes to — `UNB.S003`. */
    datenannahmestelleIk;
    /** Which Kassenart file answered — the other half of the file key. */
    insurerType;
    /** ISO date string (YYYY-MM-DD), or `null` when the Verordnung carries none. */
    prescriptionDate;
    /** ISO date of the last Leistungstag — what the Stichtag is measured against. */
    lastDeliveryDate;
    /** Delivered Behandlungseinheiten on this Verordnung. */
    deliveredUnitCount;
    /**
     * Σ of the emitted positions — the Bruttobetrag of this Abrechnungsfall.
     *
     * ⚠ `null` on a set-aside Fall, and deliberately so. A refusal carrying a
     * number would be a substituted price under another name: the operator would
     * have an amount to read off a Fall the run is not going to bill.
     */
    grossTotal;
    /**
     * The Zuzahlungs-**Soll** of the Fall: `pct + min(10, brutto − pct)`.
     *
     * Read off the price lines and rounded **on the sum**, never per position —
     * the two answers diverge, and the published figure is the claim.
     */
    copaymentTotal;
    /** What the Kasse is billed: §302 claims **net of** the Zuzahlung. */
    netTotal;
    /** `SLLA` Zuzahlungskennzeichen of the Fall; `null` where none was derived. */
    copaymentIndicator;
    /**
     * Whether the Fall's Zuzahlung is still owed by the patient — what the
     * Zuzahlungs-Balken counts.
     *
     * ⚠ **`true` wherever a Zuzahlung is due at all**, because the *Ist* does not
     * exist yet: TM-9443 records what was collected, at the desk, against a
     * receipt, and until it lands nothing in this product can say a Zuzahlung was
     * taken in. Overstating the open amount is the safe direction — it names money
     * the practice deducts from its claim and may not hold.
     */
    copaymentOpen;
    billable;
    /** `null` exactly when `billable` is true. */
    skipReason;
    /**
     * What was walked before giving up, for a skip somebody has to act on.
     * `null` on a billed Fall.
     */
    skipDetail;
    /**
     * Remarks on a Fall that **is** being billed. Always empty on a skipped one:
     * a tooltip listing four remarks beside a refusal reads as though any of them
     * might be the cause.
     */
    notes;
    /**
     * The text fields of this Fall that hold a character no Nutzdatendatei can carry
     * (#1954). Empty on almost every Fall.
     *
     * ⚠ **Not a skip reason, and not part of `billable`.** A Fall with such a field is
     * still the practice's to bill; selected, it holds the whole run up at its start,
     * named with its field, until somebody fixes the text. Setting it aside instead
     * would let a run report a success for a Fall that was quietly left out.
     */
    unrepresentableTexts;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingCandidateRowDto.prototype, "treatmentPlanId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingCandidateRowDto.prototype, "patientId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "patientFirstName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "patientLastName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "heilmittelbereich", void 0);
__decorate([
    Expose()
    /** Spec name: „Kostenträgerkennung". */
    ,
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "kostentraegerCode", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "kostentraegerIk", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "kostentraegerName", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "datenannahmestelleIk", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "insurerType", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "prescriptionDate", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingCandidateRowDto.prototype, "lastDeliveryDate", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingCandidateRowDto.prototype, "deliveredUnitCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "grossTotal", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "copaymentTotal", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "netTotal", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "copaymentIndicator", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], GkvBillingCandidateRowDto.prototype, "copaymentOpen", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Boolean)
], GkvBillingCandidateRowDto.prototype, "billable", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "skipReason", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingCandidateRowDto.prototype, "skipDetail", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Array)
], GkvBillingCandidateRowDto.prototype, "notes", void 0);
__decorate([
    Expose(),
    Type(() => GkvUnrepresentableTextDto),
    __metadata("design:type", Array)
], GkvBillingCandidateRowDto.prototype, "unrepresentableTexts", void 0);
/** One text field of a Fall and the characters in it the file cannot carry (#1954). */
export class GkvUnrepresentableTextDto {
    field;
    /** Each refused character once, in the order it first appears. */
    characters;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvUnrepresentableTextDto.prototype, "field", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Array)
], GkvUnrepresentableTextDto.prototype, "characters", void 0);
/**
 * One Nutzdatendatei this run would emit (TM-9538 / #1515).
 *
 * Keyed `(Datenannahmestelle × Kassenart × Testindikator)`, and that is the
 * whole key — ⚠ the Rechnungsart is deliberately not part of it. The reasoning
 * lives once, on `GkvNutzdatendatei` in the backend's `group-gkv-candidates.ts`.
 */
export class GkvBillingFileDto {
    datenannahmestelleIk;
    insurerType;
    /**
     * `UNB.0035`, in the run's vocabulary: `T` (Erprobung) or `E` (Echtbetrieb)
     * from the Verfahrensstand, or `P` on every file of a **test office** — a
     * Prüfdatei, never paid (#1952). A file cannot be half one and half the
     * other, so it is part of the key.
     */
    testindikator;
    /**
     * Which Kostenträgerdatei edition produced this Empfänger.
     *
     * Carried so that "which file gave us this address?" stays answerable after
     * the next edition lands — the editions turn over 15–25 times a year.
     */
    releaseId;
    caseCount;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingFileDto.prototype, "datenannahmestelleIk", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingFileDto.prototype, "insurerType", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingFileDto.prototype, "testindikator", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingFileDto.prototype, "releaseId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingFileDto.prototype, "caseCount", void 0);
/** One Kostenträger inside an Empfänger — the middle level of the tree. */
export class GkvBillingKostentraegerDto {
    kostentraegerIk;
    name;
    /** Spec name: „Kassenart". */
    insurerType;
    caseCount;
    /** Σ over the Fälle underneath. */
    grossTotal;
    /** Spec name: „Zuzahlung (gesamt)". */
    copaymentTotal;
    /** What this Kostenträger is billed — `grossTotal − copaymentTotal`. */
    netTotal;
    /** The Fälle underneath, in the order the candidate list carries them. */
    treatmentPlanIds;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingKostentraegerDto.prototype, "kostentraegerIk", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingKostentraegerDto.prototype, "name", void 0);
__decorate([
    Expose()
    /** Spec name: „Kassenart". */
    ,
    __metadata("design:type", String)
], GkvBillingKostentraegerDto.prototype, "insurerType", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingKostentraegerDto.prototype, "caseCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingKostentraegerDto.prototype, "grossTotal", void 0);
__decorate([
    Expose()
    /** Spec name: „Zuzahlung (gesamt)". */
    ,
    __metadata("design:type", Number)
], GkvBillingKostentraegerDto.prototype, "copaymentTotal", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingKostentraegerDto.prototype, "netTotal", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Array)
], GkvBillingKostentraegerDto.prototype, "treatmentPlanIds", void 0);
/**
 * One Datenannahmestelle — `UNB.S003 Empfänger` — and everything under it.
 *
 * ⚠ **The Annahmestelle is not the file**, so `fileCount` stands beside
 * `caseCount`: `661430035` (IQVIA) accepts EK, BK and IK, and its single row in
 * the reference run hides three of the ten files.
 *
 * ⚠ **Two Empfänger can carry the same name at different IKs** — `105810615`
 * and `108018007` are both "ITSCare DAV". The IK is the key and the name is
 * not, so a row has to show both.
 */
export class GkvBillingRecipientDto {
    datenannahmestelleIk;
    /**
     * `null` where no in-force edition describes the IK at all.
     *
     * ⚠ Stable across runs: read off the in-force editions in a fixed order, not
     * off whichever Kassenarten this run happens to contain — four of the eight
     * recipients in the reference run are published under two different names.
     */
    name;
    caseCount;
    fileCount;
    /**
     * Σ over the Kostenträger underneath, and therefore over their Fälle.
     *
     * ⚠ The three levels are one sum taken at three altitudes, never three
     * independent tallies — a tree whose branches do not add up to its root is
     * the defect this view exists to make impossible to miss.
     */
    grossTotal;
    /** Spec name: „Zuzahlung (gesamt)". */
    copaymentTotal;
    netTotal;
    files;
    kostentraeger;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingRecipientDto.prototype, "datenannahmestelleIk", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Object)
], GkvBillingRecipientDto.prototype, "name", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRecipientDto.prototype, "caseCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRecipientDto.prototype, "fileCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRecipientDto.prototype, "grossTotal", void 0);
__decorate([
    Expose()
    /** Spec name: „Zuzahlung (gesamt)". */
    ,
    __metadata("design:type", Number)
], GkvBillingRecipientDto.prototype, "copaymentTotal", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingRecipientDto.prototype, "netTotal", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingFileDto),
    __metadata("design:type", Array)
], GkvBillingRecipientDto.prototype, "files", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingKostentraegerDto),
    __metadata("design:type", Array)
], GkvBillingRecipientDto.prototype, "kostentraeger", void 0);
/**
 * The Zuzahlungs-Balken over the list — money the practice deducts without holding it.
 *
 * §302 claims **net of** the Zuzahlung, so every euro here is one the run takes
 * off the Kasse's bill on the assumption the patient pays it at the desk. The
 * bar is the one place the *Soll* (§302) and the *Ist* (TM-9443) look at each
 * other.
 *
 * ⚠ **Informing, never blocking.** It changes no verdict: a Fall with an open
 * Zuzahlung is billed exactly as one without. A bar that could hold a run back
 * would make an unpaid ten euro stop a five-figure Abrechnung.
 *
 * ⚠ **Only over Fälle that are billed.** A set-aside Fall deducts nothing from
 * anything, so counting it here would name money that is not at stake — and
 * `COPAYMENT_DEADLINE_PENDING`, which is a *skip reason*, is therefore
 * structurally absent from this number rather than filtered out of it.
 */
export class GkvBillingOpenCopaymentDto {
    /** Σ of the open Zuzahlungen, in euro. */
    amount;
    caseCount;
    /**
     * The Fälle the amount is made of — the bar's Durchgriff.
     *
     * The ids rather than a count alone, so *„welche denn?"* is answerable
     * without a second query against a set the run has already computed.
     */
    treatmentPlanIds;
}
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingOpenCopaymentDto.prototype, "amount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingOpenCopaymentDto.prototype, "caseCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Array)
], GkvBillingOpenCopaymentDto.prototype, "treatmentPlanIds", void 0);
/**
 * Response of the Kandidatenabfrage: the flat list, plus the tree it groups to.
 *
 * Both are here on purpose. The tree answers *„an wen geht das, und wie viele
 * Dateien werden das?"* and holds only the Fälle that would be billed; the flat
 * list is the other half of the question — the set-aside Fälle and their
 * reasons, which is the half that costs money.
 *
 * ⚠ **Reading this changes nothing.** No Aggregat, no Zähler, no byte: the
 * endpoint can be opened as often as anyone likes.
 */
export class GkvBillingCandidatesResponseDto {
    officeLocationId;
    /** ISO date of the Stichtag the Leistungstage were measured against. */
    cutoff;
    /**
     * ISO date the addresses were resolved on — the run date.
     *
     * ⚠ **Not the Stichtag, and not "the newest edition".** Three different
     * things: the Stichtag bounds Leistungstage, this picks the
     * Kostenträgerdatei edition, and the newest edition may not be in force yet
     * (`EK05Q426` sits in the corpus today and is gültig ab 01.10.2026).
     */
    asOf;
    rows;
    /** Every Empfänger the billed Fälle resolve to, sorted by IK. */
    recipients;
    /** Nutzdatendateien across all Empfänger — ⚠ not the number of Empfänger. */
    fileCount;
    billableCount;
    skippedCount;
    /** Σ over every billed Fall — the run's Bruttobetrag. */
    grossTotal;
    /** Spec name: „Zuzahlung (gesamt)". */
    copaymentTotal;
    /** What the Kassen are billed in total. */
    netTotal;
    openCopayment;
}
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingCandidatesResponseDto.prototype, "officeLocationId", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingCandidatesResponseDto.prototype, "cutoff", void 0);
__decorate([
    Expose(),
    __metadata("design:type", String)
], GkvBillingCandidatesResponseDto.prototype, "asOf", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingCandidateRowDto),
    __metadata("design:type", Array)
], GkvBillingCandidatesResponseDto.prototype, "rows", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingRecipientDto),
    __metadata("design:type", Array)
], GkvBillingCandidatesResponseDto.prototype, "recipients", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingCandidatesResponseDto.prototype, "fileCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingCandidatesResponseDto.prototype, "billableCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingCandidatesResponseDto.prototype, "skippedCount", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingCandidatesResponseDto.prototype, "grossTotal", void 0);
__decorate([
    Expose()
    /** Spec name: „Zuzahlung (gesamt)". */
    ,
    __metadata("design:type", Number)
], GkvBillingCandidatesResponseDto.prototype, "copaymentTotal", void 0);
__decorate([
    Expose(),
    __metadata("design:type", Number)
], GkvBillingCandidatesResponseDto.prototype, "netTotal", void 0);
__decorate([
    Expose(),
    Type(() => GkvBillingOpenCopaymentDto),
    __metadata("design:type", GkvBillingOpenCopaymentDto)
], GkvBillingCandidatesResponseDto.prototype, "openCopayment", void 0);
//# sourceMappingURL=gkv-billing-candidate.response.dto.js.map